import React, { useEffect, useState, useRef } from 'react'
import { Formik, Form, ErrorMessage } from 'formik'
import { useNavigate } from 'react-router-dom'
import { Avatar, Box, Icon, Typography } from '@mui/material'
import FaceIcon from '@mui/icons-material/Face'
import * as Yup from 'yup'
import Bugsnag from '@bugsnag/js'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import AdxButton from 'components/custom/AdxButton'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import AdxEmployeeAvatar from 'components/reusable/employeeAvatar/AdxEmployeeAvatar'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxInput from 'components/custom/AdxInput'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { handleScroll, handleValidationErrors } from 'services/helper/handleValidationErrors'
import { _convertAPIDate } from 'services/helper/DateTime'
import { profileDepartments } from 'services/api/dropdown'
import {
  getEmploymentData,
  rehireEmployee,
} from 'services/api/modules/staff-management/staffManagementDashboard'

const validationSchema = Yup.object({
  rehire_date: Yup.date().nullable().required('Rehire date is required'),
  department_id: Yup.number().nullable().required('Department is required'),
  designation: Yup.string()
    .nullable()
    .required('Job designation is required')
    .matches(/^[A-Z\d\-()\s]+$/i, 'Job designation must be valid'),
  internal_employee_id: Yup.string()
    .nullable()
    .matches(/^[a-zA-Z0-9]*$/, 'Employee ID must be alphanumeric'),
  job_description: Yup.string().nullable(),
  reporting_to_id: Yup.object().nullable(),
  employment_type: Yup.number().nullable(),
  hiring_date: Yup.date().nullable(),
  remarks: Yup.string().nullable().max(1000, 'Remarks must be 1000 characters or less'),
})

const employeeSubtitle = (staff) => {
  if (staff?.designation && staff?.department) {
    return `${staff.designation}, ${staff.department}`
  }

  return staff?.designation || staff?.department || ''
}

const RehireEmployee = ({ staff, employeeID }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const formikRef = useRef()
  const [pageState, setPageState] = useState('FILL_DETAILS')
  const [requestError, setRequestError] = useState(null)
  const [managerOptions, setManagerOptions] = useState([])

  const { data: employment } = useQuery(['employeeEmploymentData', employeeID], async () => {
    const { data } = await getEmploymentData(employeeID)
    return data
  })

  const { data: departmentList } = useQuery('employeeProfileDepartmentList', async () => {
    const { data } = await profileDepartments()
    return data
  })

  useEffect(() => {
    if (employment?.data?.all_staff) {
      setManagerOptions(employment.data.all_staff)
    }
  }, [employment])

  const mutation = useMutation((value) => rehireEmployee(employeeID, value), {
    onSuccess: () => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeePersonalData', employeeID])
      queryClient.invalidateQueries(['employeeEmploymentData', employeeID])
      queryClient.invalidateQueries('staffListData')
      setRequestError(null)
      setPageState('SUCCESSFUL_REQUEST')
    },
    onError: (error) => {
      setRequestError(error?.data?.message || 'Unable to rehire this staff member.')

      if (error.status === 422 && error.data.errors) {
        handleValidationErrors(error, formikRef)
        handleScroll(error.data.errors)
      } else if (error.status !== 422) {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    navigate(-1)
  }

  const handleSubmit = (values) => {
    const payload = {
      rehire_date: _convertAPIDate(values.rehire_date),
      department_id: values.department_id,
      designation: values.designation,
      reporting_to_id: values.reporting_to_id?.id || null,
      employment_type: values.employment_type || null,
      hiring_date: _convertAPIDate(values.hiring_date),
      internal_employee_id: values.internal_employee_id || null,
      job_description: values.job_description || null,
      remarks: values.remarks || null,
    }

    setRequestError(null)
    mutation.mutate(payload)
  }

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  const staffCard = [{ name: staff?.name, imgUrl: staff?.profile_image_url }]
  const employmentData = employment?.data

  if (pageState === 'SUCCESSFUL_REQUEST') {
    return (
      <AdxInfoModal
        variant="standard"
        open
        muiIcon="person_add"
        iconColor="alternate.main"
        title="Staff Rehired"
        subtitle={`${staff?.name || 'This staff member'} has been returned to active employment.`}
        footerButtons
        firstBtnLabel="Close"
        firstBtnHandle={() => navigate(-1)}
      />
    )
  }

  return (
    <AdxFormModal
      open
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      responsiveScroll
      icon={<Icon>person_add</Icon>}
      title="Rehire staff"
      subtitle="Set the employee's new active employment details."
      enableEnterSubmit={false}
      sxModal={{ width: { xs: 'auto', md: 680 } }}
    >
      <Formik
        enableReinitialize
        innerRef={formikRef}
        validationSchema={validationSchema}
        initialValues={{
          rehire_date: new Date(),
          employment_type: employmentData?.employment_type_id || '',
          hiring_date: employmentData?.hiring_date || null,
          internal_employee_id: employmentData?.internal_employee_id || '',
          designation: employmentData?.designation || staff?.designation || '',
          job_description: employmentData?.job_description || '',
          department_id: employmentData?.department_id || '',
          reporting_to_id: employmentData?.reporting_to_id
            ? {
                id: employmentData.reporting_to_id,
                name: employmentData.reporting_to_name,
                designation: employmentData.reporting_to_designation,
                profile_image_url: employmentData.reporting_to_image,
              }
            : null,
          remarks: '',
        }}
        onSubmit={handleSubmit}
      >
        {({ values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
          <Form>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'default-background.main',
                  p: 2,
                  gap: 2,
                  borderRadius: '4.5px',
                }}
              >
                <Box sx={{ width: 52 }}>
                  <AdxEmployeeAvatar max={1} data={staffCard} />
                </Box>
                <Box>
                  <Typography variant="body1" color="dark-gray-text.main" fontWeight="bold">
                    {staff?.name}
                  </Typography>
                  <Typography variant="body1" color="light-gray-text.main">
                    {employeeSubtitle(staff)}
                  </Typography>
                </Box>
              </Box>

              <AdxDatePicker
                label="Rehire date"
                name="rehire_date"
                value={values.rehire_date}
                onChange={(date) => setFieldValue('rehire_date', date)}
                disableFuture
                errorMessage={<ErrorMessage name="rehire_date" component={AdxErrorMessage} />}
                isErrorShown={touched.rehire_date && errors.rehire_date}
              />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <AdxDropdown
                  placeholder="Employment type"
                  label="Employment type"
                  sx={{ width: 1 }}
                  value={values.employment_type}
                  name="employment_type"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="employment_type" component={AdxErrorMessage} />}
                  isErrorShown={touched.employment_type && errors.employment_type}
                >
                  <AdxMenuItem value={1} selectedvalue={values.employment_type}>
                    Full time
                  </AdxMenuItem>
                  <AdxMenuItem value={2} selectedvalue={values.employment_type}>
                    Intern
                  </AdxMenuItem>
                  <AdxMenuItem value={3} selectedvalue={values.employment_type}>
                    Contract
                  </AdxMenuItem>
                </AdxDropdown>
                <AdxDatePicker
                  label="Hiring date"
                  name="hiring_date"
                  value={values.hiring_date}
                  onChange={(date) => setFieldValue('hiring_date', date)}
                  disableFuture
                  sx={{ width: 1 }}
                  errorMessage={<ErrorMessage name="hiring_date" component={AdxErrorMessage} />}
                  isErrorShown={touched.hiring_date && errors.hiring_date}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <AdxInput
                  label="Employee ID"
                  placeholder="Employee ID"
                  value={values.internal_employee_id}
                  name="internal_employee_id"
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="internal_employee_id" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.internal_employee_id && errors.internal_employee_id}
                />
                <AdxInput
                  label="Job designation"
                  placeholder="E.g: Software Engineer"
                  value={values.designation}
                  name="designation"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="designation" component={AdxErrorMessage} />}
                  isErrorShown={touched.designation && errors.designation}
                />
              </Box>

              <AdxDropdown
                placeholder="Department"
                label="Department"
                name="department_id"
                value={values.department_id}
                onChange={handleChange}
                errorMessage={<ErrorMessage name="department_id" component={AdxErrorMessage} />}
                isErrorShown={touched.department_id && errors.department_id}
              >
                {departmentList?.map((item) => (
                  <AdxMenuItem key={item.id} value={item.id} selectedvalue={values.department_id}>
                    {item.name}
                  </AdxMenuItem>
                ))}
              </AdxDropdown>

              <AdxDropdownWithFilter
                label="Reporting Manager"
                allOptions={employmentData?.all_staff || []}
                setOptions={setManagerOptions}
                filters={['name', 'designation', 'department_name']}
                value={values.reporting_to_id}
                name="reporting_to_id"
                onChange={handleChange}
                renderValue={(option) =>
                  option ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {option.profile_image_url ? (
                        <Avatar
                          src={option.profile_image_url}
                          sx={{ width: 30, height: 30, mx: 1 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: 'gray-background.main',
                            width: 30,
                            height: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            mx: 1,
                          }}
                        >
                          <FaceIcon fontSize="20px" color="alternate" />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption">{option.designation}</Typography>
                      </Box>
                    </Box>
                  ) : null
                }
              >
                {managerOptions.map((item) => (
                  <AdxMenuItem key={item.id} value={item} selectedvalue={values.reporting_to_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {item.profile_image_url ? (
                        <Avatar
                          src={item.profile_image_url}
                          sx={{ width: 40, height: 40, mx: 1 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: 'gray-background.main',
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            mx: 1,
                          }}
                        >
                          <FaceIcon fontSize="20px" color="alternate" />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                        <Typography variant="body1">{item.name}</Typography>
                        <Typography variant="caption">{item.designation}</Typography>
                      </Box>
                    </Box>
                  </AdxMenuItem>
                ))}
              </AdxDropdownWithFilter>
              {touched.reporting_to_id && errors.reporting_to_id && (
                <ErrorMessage name="reporting_to_id" component={AdxErrorMessage} />
              )}

              <AdxInput
                label="Job Description"
                placeholder="E.g: Owns employee operations and reporting."
                value={values.job_description}
                name="job_description"
                onChange={handleChange}
                multiline
                minRows={3}
                errorMessage={<ErrorMessage name="job_description" component={AdxErrorMessage} />}
                isErrorShown={touched.job_description && errors.job_description}
              />

              <AdxInput
                label="Remarks"
                placeholder="Optional remarks"
                value={values.remarks}
                name="remarks"
                onChange={handleChange}
                multiline
                minRows={3}
                charLimit={1000}
                errorMessage={<ErrorMessage name="remarks" component={AdxErrorMessage} />}
                isErrorShown={touched.remarks && errors.remarks}
              />

              <Box>
                <AdxButton
                  sx={{ width: 1 }}
                  onClick={() => handleCheckRef(handleSubmit)}
                  isLoading={mutation.isLoading}
                  disabled={mutation.isLoading}
                >
                  Rehire
                </AdxButton>
                {requestError && (
                  <Typography variant="body2" color="error.main" mt={1}>
                    {requestError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </AdxFormModal>
  )
}

export default RehireEmployee
